import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ContainerTab, ContainerTabs, TextArea } from '@guildeducationinc/recess';
import DeleteIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import classNames from 'classnames';
import { LanguagesIcon } from 'lucide-react';
import { useContext, useState } from 'react';
import { useLocale } from '../../hooks';
import { FilterConditionOperator, FilterNegateOperator, } from '../../types';
import { EnumPicker } from '../EnumPicker';
import { FieldPicker } from '../FieldPicker';
import { FilterPicker } from '../FilterPicker';
import { Form } from '../Form';
import { TrackableButton } from '../TrackableButton';
import { FilterBuilderContext } from './FilterBuilder';
import { FilterConditionOpPicker } from './FilterConditionOpPicker';
import styles from './FilterConditionRow.module.scss';
import { FilterInputValues } from './FilterInputValues';
/**
 * A component that renders a row in the filter condition builder.
 */
export const FilterConditionRow = (props) => {
    const { t } = useLocale();
    const [field, setField] = useState(null);
    const [showMessages, setShowMessages] = useState(false);
    const filterBuilderProps = useContext(FilterBuilderContext);
    /**
     * Handles the change of the field in the filter condition.
     * @param field The selected field.
     */
    const handleFieldChange = (field) => {
        setField(field ?? null);
        props.onChange({
            ...props.condition,
            fieldCode: field?.code ?? '',
            op: '',
            values: [],
            dateOperator: undefined,
            dateDirection: undefined,
        });
    };
    /**
     * Handles the change of the entity in the filter condition.
     * @param value The selected entity.
     */
    const handleEntityChange = (value) => {
        let op = '';
        if (value.startsWith('filter_')) {
            op = FilterConditionOperator.IN;
        }
        props.onChange({
            ...props.condition,
            entityType: value,
            fieldCode: '',
            op,
            values: [],
            dateOperator: undefined,
            dateDirection: undefined,
        });
        setField(null);
    };
    /**
     * Handles the change of the input values in the filter condition.
     * @param values The input values.
     * @param dateOperator The date operator.
     * @param dateDirection The date direction.
     */
    const handleInputValuesChange = (values, dateOperator, dateDirection) => {
        props.onChange({
            ...props.condition,
            values,
            dateOperator: dateOperator ?? undefined,
            dateDirection: dateDirection ?? undefined,
        });
    };
    const handleMessageChange = (lang, prop, setDraft) => (e) => {
        setDraft(prev => ({
            ...prev,
            [prop]: { ...prev[prop], [lang]: e.target.value },
        }));
    };
    // eslint-disable-next-line require-await
    const persistMessage = async (message) => {
        props.onChange({ ...props.condition, message });
        setShowMessages(false);
    };
    return (_jsxs("div", { className: classNames('flex gap-1 items-center'), children: [_jsxs("div", { className: classNames('flex--1 flex items--start', styles.filterCondition, props.readonly && styles.readonly), children: [!filterBuilderProps.hideEntityType && (_jsx(EnumPicker, { labelPrefix: 'metadata.entityType', placeholderKey: 'filter.select.entity_type', enumType: filterBuilderProps.entityEnumType, value: props.condition.entityType, onChange: handleEntityChange, disabled: props.readonly, useMui: true })), props.condition && (_jsxs(_Fragment, { children: [props.condition.entityType.startsWith('filter_') && (_jsx(_Fragment, { children: _jsx(FilterPicker, { multiple: true, useMui: true, clientId: filterBuilderProps.clientId, returnType: props.condition.entityType.substring(7), value: props.condition.values, disabled: props.readonly, onChange: value => props.onChange({
                                        ...props.condition,
                                        op: FilterConditionOperator.IN,
                                        values: value.map((x) => String(x.id)),
                                    }) }) })), !props.condition.entityType.startsWith('filter_') && (_jsxs(_Fragment, { children: [props.condition.entityType && (_jsx(FieldPicker, { useMui: true, value: props.condition.fieldCode ?? '', clientId: filterBuilderProps.clientId, entityType: props.condition.entityType, onChange: handleFieldChange, onInit: field => setField(field ?? null), readonly: props.readonly })), field && (!props.readonly || props.condition.negateOp) && (_jsx(EnumPicker, { hideLabel: true, labelPrefix: 'filter.negate.operator', enumType: FilterNegateOperator, value: props.condition.negateOp === true ? FilterNegateOperator.NOT : '', onChange: value => props.onChange({
                                            ...props.condition,
                                            negateOp: value === FilterNegateOperator.NOT,
                                        }), useMui: 'primary', disabled: props.readonly, isClearable: true })), field && (_jsx(FilterConditionOpPicker, { field: field, onChange: value => props.onChange({ ...props.condition, op: value }), value: props.condition.op, readonly: props.readonly })), props.condition.op && field && (_jsx(_Fragment, { children: _jsx(FilterInputValues, { condition: props.condition, field: field, onChange: handleInputValuesChange, readonly: props.readonly }) }))] })), props.customizedMessaging && (_jsx(TrackableButton, { btnType: 'icon', enText: 'Messages', tag: `filter_condition_${props.condition.fieldCode}`, appearance: 'minimal', style: { width: 36 }, label: t('Messages'), icon: _jsx(LanguagesIcon, { size: '20' }), onClick: () => setShowMessages(true) }))] }))] }), !props.readonly && (_jsx(TrackableButton, { btnType: 'icon', enText: 'Delete', tag: `filter_condition_${props.condition.fieldCode}`, label: t('Delete'), appearance: 'minimal', icon: _jsx(DeleteIcon, { size: 24 }), onClick: props.onDelete })), showMessages && (_jsx(Form, { fields: [], title: 'Messages', modal: true, onValidate: () => ({}), obj: props.condition.message || { success: {}, failure: {} }, onSave: persistMessage, minHeight: 150, onCancel: () => setShowMessages(false), children: ({ draft, setDraft }) => (_jsx("div", { children: _jsxs(ContainerTabs, { children: [_jsx(ContainerTab, { title: t('Success'), children: filterBuilderProps.languages?.map(lang => (_jsx(TextArea, { fullWidth: true, autosize: true, label: t(lang), value: draft.success[lang] ?? '', onChange: handleMessageChange(lang, 'success', setDraft), readOnly: props.readonly, className: styles.messageText }, lang))) }), _jsx(ContainerTab, { title: t('Failure'), children: filterBuilderProps.languages?.map(lang => (_jsx(TextArea, { fullWidth: true, autosize: true, label: t(lang), value: draft.failure[lang] ?? '', onChange: handleMessageChange(lang, 'failure', setDraft), readOnly: props.readonly, className: styles.messageText }, lang))) })] }) })) }))] }));
};
